export function formatCallDurationLabel(
    durationMs: number | null,
): string | null {
    if (!durationMs || durationMs <= 0) {
        return null;
    }

    const totalSeconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (minutes === 0) {
        return `${seconds}s`;
    }

    return `${minutes}m${seconds}s`;
}

export function formatCallBadgeLabel(value: string): string {
    return value
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

// Fallback for backend enum values with no explicit customer-facing label:
// sentence case reads as copy, while Title Casing Every Word reads as a
// leaked identifier.
function sentenceCaseFromSnakeCase(value: string): string {
    const words = value.split('_').join(' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// Customer-facing labels for `display_status`, which mixes
// ContactAttemptOutreachStatus outcomes with the ongoing/ended lifecycle
// statuses. Values here must stay honest about what the backend actually
// observed — e.g. `unable_to_leave_message` means the voicemail box did not
// accept a message, not that one was left.
const CALL_DISPLAY_STATUS_LABELS: Record<string, string> = {
    live_answer: 'Answered',
    voicemail_left: 'Voicemail left',
    unable_to_leave_message: 'Couldn’t leave voicemail',
    call_disconnected: 'Call dropped',
    no_answer: 'No answer',
    failed: 'Failed',
    unreachable: 'Unreachable',
    completed: 'Completed',
    transferred: 'Transferred',
    ongoing: 'In progress',
    ended: 'Ended',
    queued: 'Queued',
    scheduled: 'Scheduled',
    sent: 'Sent',
    delivered: 'Delivered',
    manually_notified: 'Notified manually',
    reviewing_call: 'Reviewing call',
};

export function formatCallDisplayStatusLabel(status: string): string {
    return (
        CALL_DISPLAY_STATUS_LABELS[status] ?? sentenceCaseFromSnakeCase(status)
    );
}

// Customer-facing labels for `routing_path` — how the inbound webhook routed
// the call before any agent behavior. `voice_agent` is recorded before the
// handoff completes (the call can still fail or fall back to a transfer), so
// the label says where the call was routed, not that Phoebe answered.
const ROUTING_PATH_LABELS: Record<string, string> = {
    voice_agent: 'Routed to Phoebe',
    direct_transfer: 'Transferred without answering',
    classic_voicemail: 'Sent to voicemail',
    hangup: 'Not answered',
};

export function formatRoutingPathLabel(path: string): string {
    return ROUTING_PATH_LABELS[path] ?? sentenceCaseFromSnakeCase(path);
}

export function isVoiceCallListeningOnly(
    call: {
        readonly routing_reason?: string | null;
    },
    silentListeningLabelSettingsEnabled: boolean = false,
): boolean {
    return (
        silentListeningLabelSettingsEnabled &&
        call.routing_reason === 'call_handling_answers_no_one'
    );
}

// Customer-facing labels for `routing_reason` — why the webhook chose the
// routing path it did.
const ROUTING_REASON_LABELS: Record<string, string> = {
    call_handling_answers_no_one: 'Phoebe is set not to answer this line',
    non_caregiver_caller: 'Caller is not a known caregiver',
    caregiver_not_found: 'No matching caregiver found',
    voice_agent_disabled: 'Phoebe answering is turned off',
    voice_settings_missing: 'Phone setup is incomplete',
    unknown_caller_auto_transfer: 'Unknown caller transferred automatically',
    caller_id_unavailable: 'Caller ID unavailable',
    invalid_caller_phone_number: 'Invalid caller phone number',
    twilio_client_caller_id: 'Browser-based caller',
    classic_voicemail: 'Sent to voicemail',
    transfer_handback: 'Returned from a transfer',
};

// Internal error-path routing reasons collapse to one generic customer label;
// the diagnostic distinction stays available in Voice QA via
// formatRoutingReasonDiagnosticLabel. Keep this set in sync with the reasons
// emitted by services/api/routes/webhooks/twilio.py and
// twilio_inbound_voice_handoff.py.
const INTERNAL_ERROR_ROUTING_REASONS = new Set([
    'fail_open_transfer',
    'inbound_voice_unhandled_exception',
    'twilio_voice_fallback_url',
    'routing_path_mismatch',
    'voice_stream_start_timeout',
    'voice_media_session_wait_failed',
    'voice_capacity_check_failed',
    'voice_capacity_unavailable',
    'voice_start_context_persistence_failed',
    'voice_stream_handoff_setup_failed',
    'voice_backend_startup_timeout',
    'voice_backend_unreachable',
    'voice_worker_assignment_timeout',
    'voice_worker_unavailable',
]);

export function formatRoutingReasonLabel(
    reason: string,
    scheduleName?: string | null,
): string {
    if (reason === 'receptionist_schedule' && scheduleName) {
        return `Receptionist schedule: ${scheduleName}`;
    }
    if (reason === 'after_hours_receptionist') {
        return 'After hours receptionist';
    }
    if (INTERNAL_ERROR_ROUTING_REASONS.has(reason)) {
        return 'Technical issue while connecting';
    }
    return ROUTING_REASON_LABELS[reason] ?? sentenceCaseFromSnakeCase(reason);
}

// Diagnostic variant for admin surfaces (Voice QA): keeps the raw reason
// value so distinct failure paths stay distinguishable and greppable, instead
// of the customer formatter's generic technical-issue collapse.
export function formatRoutingReasonDiagnosticLabel(
    reason: string,
    scheduleName?: string | null,
): string {
    if (reason === 'receptionist_schedule' && scheduleName) {
        return `Receptionist schedule: ${scheduleName}`;
    }
    if (reason === 'after_hours_receptionist') {
        return 'After hours receptionist';
    }
    return reason;
}
